import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { generateId } from "../../utils/generateId";
import { RootState } from "../rootReducer";
import { ShoppingList } from "../../models/shopping-list";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";
import { deleteShoppingListAction, mergeShoppingListsAction } from "../actions";
import { translate } from "../../i18n/i18n";

export const shoppingListsAdapter = createEntityAdapter<ShoppingList>();
type ShoppingListsState = EntityState<ShoppingList, string>;
const initialState: ShoppingListsState = shoppingListsAdapter.getInitialState();

const liveLists = (state: ShoppingListsState): ShoppingList[] =>
  Object.values(state.entities).filter((l): l is ShoppingList => !!l && !l.deletedAt);

const nextSortOrder = (state: ShoppingListsState): number =>
  liveLists(state).reduce((max, l) => Math.max(max, l.sortOrder ?? 0), -1) + 1;

// Default name for a new (non-primary) list: "Shopping list #N", where N is one
// past the highest existing "#N" (min 2, so the first extra list is #2). Using the
// max — not the count — so a delete-then-create doesn't reuse a just-freed number.
// Localised at creation time (the "#N" suffix is language-agnostic, so numbering
// still tracks across locales); the primary is unnamed and resolves its label at
// display time instead (see shoppingListDisplayName).
const nextDefaultName = (state: ShoppingListsState): string => {
  const maxNum = liveLists(state).reduce((max, l) => {
    const m = l.name.match(/#(\d+)\s*$/);
    return m ? Math.max(max, Number(m[1])) : max;
  }, 1);
  return `${translate("shopping.defaultListName")} #${maxNum + 1}`;
};

export const shoppingListsSlice = createSlice({
  name: "shoppingLists",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(shoppingListsAdapter),
    removeManyFromSync: makeSyncRemove(shoppingListsAdapter),

    // Create the primary list if the user has none yet — the bootstrap guard so
    // every account/device has exactly one primary. No-op when a live list exists.
    ensurePrimaryList: {
      reducer(state: ShoppingListsState, action: PayloadAction<{ id: string }>) {
        if (liveLists(state).length > 0) return;
        // Created unnamed — the default label is resolved at display time
        // (shoppingListDisplayName) so it follows the active locale until renamed.
        shoppingListsAdapter.addOne(state, {
          id: action.payload.id,
          name: "",
          sortOrder: 0,
          isPrimary: true,
        });
      },
      prepare(payload: { id?: string } = {}) {
        return { payload: { id: payload.id ?? generateId() } };
      },
    },

    addShoppingList: {
      reducer(
        state: ShoppingListsState,
        action: PayloadAction<{ id: string; name?: string }>,
      ) {
        const name = action.payload.name?.trim() || nextDefaultName(state);
        shoppingListsAdapter.addOne(state, {
          id: action.payload.id,
          name,
          sortOrder: nextSortOrder(state),
        });
      },
      prepare(payload: { name?: string } = {}) {
        return { payload: { id: generateId(), ...payload } };
      },
    },

    renameShoppingList(
      state: ShoppingListsState,
      action: PayloadAction<{ id: string; name: string }>,
    ) {
      const name = action.payload.name.trim();
      if (!name || !state.entities[action.payload.id]) return;
      shoppingListsAdapter.updateOne(state, { id: action.payload.id, changes: { name } });
    },

    reorderShoppingLists(
      state: ShoppingListsState,
      action: PayloadAction<{ listIds: string[] }>,
    ) {
      const updates = action.payload.listIds
        .map((id, index) => ({ id, index }))
        .filter(({ id, index }) => {
          const l = state.entities[id];
          return l && !l.deletedAt && l.sortOrder !== index;
        })
        .map(({ id, index }) => ({ id, changes: { sortOrder: index } }));
      shoppingListsAdapter.updateMany(state, updates);
    },
  },
  extraReducers: (builder) => {
    // Soft-delete a list (never the primary); items cascade off the same action in
    // the items slice.
    builder.addCase(deleteShoppingListAction, (state, action) => {
      const list = state.entities[action.payload];
      if (!list || list.isPrimary) return;
      shoppingListsAdapter.updateOne(state, {
        id: action.payload,
        changes: { deletedAt: new Date().toISOString() },
      });
    });

    // Merge: soft-delete the merged-away lists (and clear isPrimary so a demoted
    // duplicate primary can't linger as a second primary). The items slice
    // re-parents their items to intoId off the same action.
    builder.addCase(mergeShoppingListsAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = action.payload.fromIds
        .filter((id) => state.entities[id] && !state.entities[id]!.deletedAt)
        .map((id) => ({ id, changes: { deletedAt: now, isPrimary: false } }));
      shoppingListsAdapter.updateMany(state, updates);
    });
  },
});

const listsSelectors = shoppingListsAdapter.getSelectors(
  (s: RootState) => s.shoppingLists,
);

export const selectShoppingListById = listsSelectors.selectById;

// Live lists in manual order (primary first as a stable tiebreak).
export const selectAllShoppingLists = createSelector(listsSelectors.selectAll, (lists) =>
  lists
    .filter((l) => !l.deletedAt)
    .sort((a, b) => {
      if (!!a.isPrimary !== !!b.isPrimary) return a.isPrimary ? -1 : 1;
      const diff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      return diff !== 0 ? diff : a.name.localeCompare(b.name);
    }),
);

// The primary list's id (or the first live list, or undefined before bootstrap).
export const selectPrimaryListId = createSelector(
  selectAllShoppingLists,
  (lists) => (lists.find((l) => l.isPrimary) ?? lists[0])?.id,
);

// The active ("current") list's id — the device-local preference when it points at
// a live list, otherwise the primary. This is the single source of truth for "which
// list am I on", used by the shopping screen, homepage peek and add-to-list.
export const selectActiveListId = createSelector(
  selectAllShoppingLists,
  selectPrimaryListId,
  (s: RootState) => s.preferences.activeShoppingListId,
  (lists, primaryId, activeId) =>
    activeId && lists.some((l) => l.id === activeId) ? activeId : primaryId,
);

// The active list object (name etc.); undefined only before the primary exists.
export const selectActiveList = createSelector(
  selectAllShoppingLists,
  selectActiveListId,
  (lists, activeId) => lists.find((l) => l.id === activeId),
);

export interface ShoppingListSummary extends ShoppingList {
  count: number; // live items on this list
}

// Every list with its live item count (for the switcher/index). Items with no
// listId count toward the primary (legacy rows before backfill).
export const selectShoppingListSummaries = createSelector(
  selectAllShoppingLists,
  selectPrimaryListId,
  (s: RootState) => s.shoppingListItems.entities,
  (lists, primaryId, itemEntities): ShoppingListSummary[] => {
    const counts = new Map<string, number>();
    for (const it of Object.values(itemEntities)) {
      if (!it || it.deletedAt) continue;
      const lid = it.listId ?? primaryId;
      if (lid) counts.set(lid, (counts.get(lid) ?? 0) + 1);
    }
    return lists.map((l) => ({ ...l, count: counts.get(l.id) ?? 0 }));
  },
);

// TODO(monetization): replace with the real premium_until entitlement. Stubbed to
// true so the multi-list UI is fully functional until the paywall is wired.
const IS_PREMIUM_STUB = true;

// Whether the user may create another list: always allowed while they only have
// the one (primary), otherwise premium-gated.
export const selectCanCreateShoppingList = createSelector(
  selectAllShoppingLists,
  (lists) => lists.length < 1 || IS_PREMIUM_STUB,
);

export const {
  ensurePrimaryList,
  addShoppingList,
  renameShoppingList,
  reorderShoppingLists,
  upsertManyFromSync: upsertShoppingListsFromSync,
  removeManyFromSync: removeShoppingListsFromSync,
} = shoppingListsSlice.actions;

export default shoppingListsSlice.reducer;
